import { memo } from 'react';
import { Box, Chip, Paper, Stack, Typography, alpha } from '@mui/material';

/** One answered question, parsed from a `<poll-answers>` user-message payload. */
export type PollAnswerItem = { header?: string; question: string; answers: string[] };

/**
 * Renders a user's ask-user submission (sent as a `<poll-answers>` payload) as a compact answers card
 * instead of raw text: per question a header chip (when present), the question, and the chosen answers
 * as chips. Aligned to the right like the user's own messages.
 */
export const AndiePollAnswers = memo(({ items }: { items: PollAnswerItem[] }) => (
    <Paper
        sx={(theme) => ({
            px: 1.75,
            py: 1.25,
            maxWidth: '80%',
            alignSelf: 'flex-end',
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.andie.main, 0.24)}`,
            backgroundColor: alpha(theme.palette.andie.main, 0.06),
        })}
    >
        <Stack gap={1.25}>
            {items.map((item, index) => (
                <Box key={index}>
                    {item.header && (
                        <Chip
                            label={item.header}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                mb: 0.5,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    )}
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5 }}>
                        {item.question}
                    </Typography>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {item.answers.length > 0 ? (
                            item.answers.map((answer, i) => (
                                <Chip
                                    key={i}
                                    label={answer}
                                    size="small"
                                    color="primary"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.75rem',
                                        '& .MuiChip-label': { px: 0.9 },
                                    }}
                                />
                            ))
                        ) : (
                            <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>-</Typography>
                        )}
                    </Stack>
                </Box>
            ))}
        </Stack>
    </Paper>
));

AndiePollAnswers.displayName = 'AndiePollAnswers';
